import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import {
  discountDoesNotExceedTotal,
  nonNegativeValidator,
  quantityValidator,
} from '../../shared/services/validations';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { InvoiceService } from '../../features/invoice/services/invoice.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PreferenceCodeService } from '../../features/preference-code/services/preference-code.service';

@Component({
  selector: 'app-invoice-new',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    ReactiveFormsModule,
    FormsModule,
    NgxMatSelectSearchModule,
    MatDatepickerModule,
  ],
  templateUrl: './invoice-new.component.html',
  styleUrl: './invoice-new.component.scss',
})
export class InvoiceNewComponent {
  newInvoiceForm: FormGroup;
  vendorData: any = {};
  invoiceId: any;
  validationError: boolean = false;
  documentArray: any[] = [];
  public genderData: any[] = [];
  public preferenceData: any[] = [];
  today: Date = new Date();
  currencyData: any;
  invoiceData: any;
  invoiceNo:string='';

  overallSubTotal: number = 0;
  overallVat: number = 0;
  overallDiscount: number = 0;
  overallTotalAmount: number = 0;
  overAllNetAmount: number = 0;

  customers: any[] = [];
  orderData: any[] = [];
  private searchSubject = new Subject<string>();
  private orderSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private invoiceService: InvoiceService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private preferenceCode: PreferenceCodeService
  ) {}

  ngOnInit() {
    this.initForm();
    this.getCustomers();
    this.route.paramMap.subscribe((params) => {
      this.invoiceId = params.get('id');
      if (this.invoiceId) {
        this.fetchInvoiceById(this.invoiceId);
      }
    });
    this.newInvoiceForm.get('discount')?.valueChanges.subscribe(() => {
      this.updateOverallSummary();
    });
    if (!this.invoiceId) {
      this.addItem();
    }
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getCustomers(searchTerm);
      });

    this.newInvoiceForm
      .get('customerId')
      ?.valueChanges.subscribe((customerId) => {
        if (customerId) {
          this.getOrders('', customerId);
        }
      });

    this.orderSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        const customerId = this.newInvoiceForm.get('customerId')?.value;
        if (customerId) {
          this.getOrders(searchTerm, customerId);
        }
      });
      if (!this.invoiceId) {
        this.fetchPreferenceCode();
      }
  }

  fetchPreferenceCode(search: string = '') {
    this.preferenceCode.fetchPreferenceCodes(search,'Invoice').subscribe({
      next: (res) => {
        res.forEach((prefix: any) => {
          this.invoiceNo= prefix.sequenceCode;
        });
        this.newInvoiceForm.patchValue({ invoiceNo: this.invoiceNo});      
      },
      error: (err) => {
        console.log(err);
      },
    })
  }

  initForm() {
    if (this.invoiceData) {
      console.log(this.invoiceData);

      this.newInvoiceForm = this.fb.group({
        id: Number(this.invoiceId),
        invoiceNo: [this.invoiceData?.invoiceNo || '', [Validators.required]],
        customerId: [
          this.invoiceData?.customerId || null,
          [Validators.required],
        ],
        orderId: [this.invoiceData?.orderId || null, [Validators.required]],
        dueDate: [
          this.invoiceData?.dueDate ? new Date(this.invoiceData.dueDate) : '',
          [Validators.required],
        ],
        items: this.fb.array([]),
        discount: [
          this.invoiceData?.discount || 0,
          [nonNegativeValidator, discountDoesNotExceedTotal()],
        ],
      });

      // Patch item list
      if (this.invoiceData.invoiceItems?.length) {
        const itemsArray = this.newInvoiceForm.get('items') as FormArray;
        this.invoiceData.invoiceItems.forEach((item: any) => {
          const itemGroup = this.fb.group({
            itemId: [item.id],
            itemName: [item.itemName, Validators.required],
            description: [item.description],
            quantity: [
              item.quantity,
              [
                Validators.required,
                Validators.pattern(/^\d+(\.\d+)?$/),
                quantityValidator,
              ],
            ],
            rate: [item.rate, Validators.required],
            subTotal: [item.subTotal],
            tax: [item.tax],
            totalAmount: [item.totalAmount],
          });

          itemGroup.valueChanges.subscribe((value) => {
            const quantity = parseFloat(value.quantity) || 0;
            const rate = parseFloat(value.rate) || 0;
            const subTotal = quantity * rate;
            const tax = subTotal * 0.05;
            const totalAmount = subTotal + tax;

            itemGroup.patchValue(
              {
                subTotal,
                tax,
                totalAmount,
              },
              { emitEvent: false }
            );

            this.updateOverallSummary();
          });

          itemsArray.push(itemGroup);
        });

        this.updateOverallSummary();
      }

      this.newInvoiceForm
        .get('customerId')
        ?.valueChanges.subscribe((customerId) => {
          if (customerId) {
            this.getOrders('', customerId);
          }
        });

      this.newInvoiceForm.get('discount')?.valueChanges.subscribe(() => {
        this.updateOverallSummary();
      });
    } else {
      this.newInvoiceForm = this.fb.group({
        invoiceNo: ['', [Validators.required]],
        customerId: [null, [Validators.required]],
        orderId: [null, [Validators.required]],
        dueDate: ['', [Validators.required]],
        discount: [0, [nonNegativeValidator, discountDoesNotExceedTotal()]],
        items: this.fb.array([]),
      });
    }
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onOrderSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.orderSearchSubject.next(searchTerm);
  }

  getCustomers(search: string = '') {
    this.invoiceService.fetchCustomers(search).subscribe({
      next: (data) => {
        // console.log(data);
        this.customers = data;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  allowOnlyNumberWithDecimal(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow digits and decimal point
    if (!/^[0-9.]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Disallow more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  blockPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!/^\d*\.?\d*$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  allowOnlyInteger(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow only digits 0-9
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
    }
  }

  blockPasteNonInteger(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    // Allow only digits, no decimal or special chars
    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  getOrders(
    search: string = '',
    customerId: any = null,
    callback?: () => void
  ) {
    this.invoiceService
      .fetchOrders(search, Number(customerId), false)
      .subscribe({
        next: (data) => {
          if (data?.length) {
            this.orderData = data;
          } else {
            this.orderData = [];
            this.newInvoiceForm.patchValue({ orderId: null });
            this.newInvoiceForm.get('orderId')?.markAsPristine();
            this.newInvoiceForm.get('orderId')?.markAsUntouched();
            this.toastr.warning('Selected customer has no orders');
          }

          if (callback) callback(); // call initForm
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  fetchInvoiceById(id: any) {
    this.invoiceService.listInvoiceById(Number(id)).subscribe({
      next: (data) => {
        this.invoiceData = data;
        this.getOrders('', data.customerId, () => {
          this.initForm();
        });
        // this.initForm();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  get items(): FormArray {
    return this.newInvoiceForm.get('items') as FormArray;
  }

  onRemoveNewlyAddedItems(index: number): void {
    this.items.removeAt(index);
    this.updateOverallSummary();
  }

  addItem(): void {
    const itemGroup = this.fb.group({
      itemId: [null],
      itemName: ['', Validators.required],
      description: [''],
      quantity: [
        1,
        [
          Validators.required,
          Validators.pattern(/^\d+(\.\d+)?$/),
          quantityValidator,
        ],
      ],
      rate: [0, Validators.required],
      subTotal: [0],
      tax: [0],
      totalAmount: [0],
    });

    this.items.push(itemGroup);

    // Auto calculate on change
    itemGroup.valueChanges.subscribe((value) => {
      const quantity = parseFloat((value.quantity ?? '0').toString()) || 0;
      const rate = parseFloat((value.rate ?? '0').toString()) || 0;
      // const quantity = parseFloat(value.quantity) || 0;
      // const rate = parseFloat(value.rate) || 0;
      const subTotal = quantity * rate;
      const tax = subTotal * 0.05;
      const totalAmount = subTotal + tax;

      itemGroup.patchValue(
        {
          subTotal,
          tax,
          totalAmount,
        },
        { emitEvent: false }
      );

      this.updateOverallSummary();
    });
  }

  updateOverallSummary(): void {
    const items = this.newInvoiceForm.get('items') as FormArray;
    let subTotal = 0;
    let tax = 0;

    items.controls.forEach((item) => {
      subTotal += parseFloat(item.get('subTotal')?.value) || 0;
      tax += parseFloat(item.get('tax')?.value) || 0;
    });

    const discount =
      parseFloat(this.newInvoiceForm.get('discount')?.value) || 0;
    const totalAmount = subTotal + tax - discount;

    this.overallSubTotal = subTotal;
    this.overallVat = tax;
    this.overallDiscount = discount;
    this.overallTotalAmount = totalAmount;
  }

  async onSubmit(status: 'Draft' | 'Pending' = 'Draft') {
    if (this.newInvoiceForm.invalid) {
      this.newInvoiceForm.markAllAsTouched();
      return;
    }

    const formValue = this.newInvoiceForm.value;

    if (!formValue.items || formValue.items.length === 0) {
      this.toastr.error(
        'Please add at least one item before submitting the invoice.'
      );
      return;
    }

    const createInvoiceInput = {
      invoiceNo: formValue.invoiceNo,
      customerId: formValue.customerId,
      orderId: formValue.orderId,
      subTotal: this.overallSubTotal,
      discount: this.overallDiscount,
      tax: this.overallVat,
      totalAmount: this.overallTotalAmount,
      invoiceStatus: status, // either 'Draft' or 'Pending'
      invoiceDate: new Date().toISOString(),
      dueDate: new Date(formValue.dueDate).toISOString(),
      items: formValue.items.map((item: any) => ({
        itemName: item.itemName,
        description: item.description,
        quantity: Number(item.quantity),
        rate: Number(item.rate),
        tax: Number(item.tax),
        subTotal: Number(item.subTotal),
        totalAmount: Number(item.totalAmount),
      })),
    };

    if (!this.invoiceId) {
      this.invoiceService.createNewInvoice(createInvoiceInput).subscribe({
        next: async (data) => {
          this.toastr.success('Invoice created successfully', 'Success');
          this.onCancel();
          this.router.navigate([`/invoice`]);
        },
        error: (error) => this.toastr.error(error, 'Error'),
      });
    } else {
      this.invoiceService
        .updateInvoiceById(createInvoiceInput, Number(this.invoiceId))
        .subscribe({
          next: async (data) => {
            this.toastr.success('Invoice updated successfully', 'Success');
            this.onCancel();
            this.router.navigate([`/invoice`]);
          },
          error: (error) => this.toastr.error(error, 'Error'),
        });
    }
  }

  onCancel() {
    if (!this.invoiceId) {
      this.router.navigate([`/invoice`]);
    } else {
      this.router.navigate([`/invoice`]);
    }
  }
}
